import { useGenericSocket } from "@/hooks/useGenericSocket";
import { SocketTopics } from "@/lib/natsConnection";
import { useCallback } from "react";
import { z } from "zod";

/**
 * VWAP Screener row type
 */
export interface IVWAPScreenerRow {
  symbol: string;
  period: string;
  value: number;
  toClosePrc: number;
}

/**
 * VWAP Screener response type
 */
export interface IVWAPScreenerResponse {
  timestamp: number;
  timeFrame: string;
  data: IVWAPScreenerRow[];
}

const VWAPScreenerRowSchema = z.object({
  symbol: z.string(),
  period: z.string(),
  value: z.number(),
  toClosePrc: z.number(),
});

const VWAPScreenerResponseSchema = z.object({
  timestamp: z.number(),
  timeFrame: z.string(),
  data: z.array(VWAPScreenerRowSchema),
});

/**
 * Hook to subscribe to VWAP Screener NATS topic and return validated data
 */
export function useVWAPScreener() {
  const handler = useCallback(
    (_: IVWAPScreenerResponse, newData: IVWAPScreenerResponse) => {
      // Validate and return only if valid
      const parsed = VWAPScreenerResponseSchema.safeParse(newData);
      if (parsed.success) {
        return parsed.data;
      }
      return { timestamp: 0, timeFrame: "", data: [] };
    },
    [],
  );

  return useGenericSocket<IVWAPScreenerResponse, IVWAPScreenerResponse>(
    SocketTopics.vwapScreener,
    handler,
  );
}
